import {React, useContext, useEffect, useState, useRef} from 'react'
import getToken from '../functions/API/getToken';
import { toast } from '../lib/toast.js';
import GlobalContext from "../context/GlobalContext.js";
import MFACode from '../components/MFACode.js';
import {
  Home,
  Wrench,
  Bell,
  CreditCard,
  FileText,
  CalendarDays,
  MessageCircle,
  Settings,
  CircleUserRound,
  Eye,
  EyeOff,
  ShieldCheck,
  X,
} from 'lucide-react';
import { darkenColor } from '../functions/colorUtils.js';

// Orbital icon definitions
const innerOrbitIcons = [
  { Icon: Wrench, color: '#6366F1', bg: '#EEF2FF', angle: 0 },
  { Icon: CircleUserRound, color: '#0EA5E9', bg: '#E0F2FE', angle: 120 },
  { Icon: Bell, color: '#F97316', bg: '#FFF7ED', angle: 240 },
];

const outerOrbitIcons = [
  { Icon: CreditCard, color: '#F59E0B', bg: '#FEF3C7', angle: 0 },
  { Icon: FileText, color: '#10B981', bg: '#D1FAE5', angle: 72 },
  { Icon: CalendarDays, color: '#EC4899', bg: '#FCE7F3', angle: 144 },
  { Icon: MessageCircle, color: '#8B5CF6', bg: '#F5F3FF', angle: 216 },
  { Icon: Settings, color: '#6B7280', bg: '#F3F4F6', angle: 288 },
];

function OrbitIcon({ Icon, color, bg, size = 48 }) {
  return (
    <div
      className="rounded-full flex items-center justify-center shadow-sm"
      style={{ width: size, height: size, backgroundColor: bg }}
    >
      <Icon size={size * 0.43} color={color} strokeWidth={1.8} />
    </div>
  );
}

function OrbitalAnimation({ primaryColor }) {
  const innerRadius = 110;
  const outerRadius = 175;
  const center = 205;

  return (
    <div className="relative mx-auto" style={{ width: center * 2, height: center * 2 }}>
      {/* Orbit rings */}
      <div
        className="absolute rounded-full border border-gray-300/50"
        style={{
          width: innerRadius * 2,
          height: innerRadius * 2,
          top: center - innerRadius,
          left: center - innerRadius,
        }}
      />
      <div
        className="absolute rounded-full border border-gray-300/50"
        style={{
          width: outerRadius * 2,
          height: outerRadius * 2,
          top: center - outerRadius,
          left: center - outerRadius,
        }}
      />

      {/* Center house icon */}
      <div
        className="absolute rounded-full bg-white shadow-lg flex items-center justify-center animate-float"
        style={{
          width: 68,
          height: 68,
          top: center - 34,
          left: center - 34,
        }}
      >
        <Home size={30} color={primaryColor} strokeWidth={2} />
      </div>

      {/* Inner orbit icons — rotate clockwise */}
      <div
        className="absolute inset-0 animate-orbit-inner"
        style={{ transformOrigin: `${center}px ${center}px` }}
      >
        {innerOrbitIcons.map(({ Icon, color, bg, angle }, i) => {
          const rad = (angle * Math.PI) / 180;
          const x = center + innerRadius * Math.cos(rad) - 24;
          const y = center + innerRadius * Math.sin(rad) - 24;
          return (
            <div
              key={i}
              className="absolute animate-counter-rotate-inner"
              style={{ left: x, top: y }}
            >
              <OrbitIcon Icon={Icon} color={color} bg={bg} />
            </div>
          );
        })}
      </div>

      {/* Outer orbit icons — rotate counter-clockwise */}
      <div
        className="absolute inset-0 animate-orbit-outer"
        style={{ transformOrigin: `${center}px ${center}px` }}
      >
        {outerOrbitIcons.map(({ Icon, color, bg, angle }, i) => {
          const rad = (angle * Math.PI) / 180;
          const x = center + outerRadius * Math.cos(rad) - 24;
          const y = center + outerRadius * Math.sin(rad) - 24;
          return (
            <div
              key={i}
              className="absolute animate-counter-rotate-outer"
              style={{ left: x, top: y }}
            >
              <OrbitIcon Icon={Icon} color={color} bg={bg} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Login() {

  const [email, setEmail] = useState();
  const [password, setPassword] = useState();
  const [showPassword, setShowPassword] = useState(false);
  const {theme, updateTheme, themeCSS, settings} = useContext(GlobalContext);
  const [loadingspinner, setLoadingSpinner] = useState(false);
  const [displayStage, setDisplayStage] = useState(0); // 0=welcome, 1=login form, 2=MFA

  // SSO state
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [emailChecked, setEmailChecked] = useState(false);
  const emailCheckTimeoutRef = useRef(null);

  const primaryColor = settings?.primary_hex_colour || '#813468';
  const primaryDark = darkenColor(primaryColor);

  async function initialLogin() {
    setLoadingSpinner(true)
    const loginresponse = await getToken(email, password, "Web Application");
    if(loginresponse.response.status == 200) setDisplayStage(2);
    else if(loginresponse.response.status != 200){
      toast.error('Incorrect username or password!');
    }
    setLoadingSpinner(false)
  }

  async function completeLogin(mfacode){
    setLoadingSpinner(true)
    const LoginResponse = await getToken(email, password, "Web Application", mfacode);
    if(LoginResponse.response.status === 403 && LoginResponse.data.reason === "Invalid"){
      toast.error('Invalid code please try again.');
    } else if (LoginResponse.response.status === 403 && LoginResponse.data.reason === "Expired"){
      setDisplayStage(1);
      toast.error('Code expired - please re-login');
    } else if (LoginResponse.response.status === 200){
      localStorage.setItem("apiToken", LoginResponse.data.token)
      localStorage.setItem("role", LoginResponse.data.role)
      if(LoginResponse.data.role === "admin"){
        window.location.href = '/admin'
      } else {
        window.location.href = '/'
      }
    }
    setLoadingSpinner(false)
  }

  function emailChange(event) {
    const newEmail = event.target.value;
    setEmail(newEmail);
    setSsoEnabled(false);
    setEmailChecked(false);
    if (emailCheckTimeoutRef.current) {
      clearTimeout(emailCheckTimeoutRef.current);
    }
    if (newEmail && newEmail.includes('@')) {
      emailCheckTimeoutRef.current = setTimeout(() => {
        checkEmailForSSO(newEmail);
      }, 500);
    }
  }

  function passwordChange(event) {
    setPassword(event.target.value);
  }

  function handleEnter(event) {
    if (event.key === "Enter"){
      initialLogin()
    }
  }

  function setParentCode(code) {
    completeLogin(code)
  }

  async function checkEmailForSSO(emailToCheck) {
    if (!emailToCheck) {
      setSsoEnabled(false);
      setEmailChecked(false);
      return;
    }
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/sso/check-email?email=${encodeURIComponent(emailToCheck)}`
      );
      const data = await response.json();
      setSsoEnabled(data.sso_enabled);
      setEmailChecked(true);
    } catch {
      setSsoEnabled(false);
      setEmailChecked(true);
    }
  }

  async function initiateSSO() {
    setSsoLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/sso/authorize?email=${encodeURIComponent(email)}`
      );
      const data = await response.json();
      if (!response.ok || !data.authorization_url) {
        toast.error(data.message || 'Failed to initiate SSO login');
        setSsoLoading(false);
        return;
      }
      window.location.href = data.authorization_url;
    } catch (error) {
      toast.error('Failed to initiate SSO login');
      setSsoLoading(false);
    }
  }

  // ── MFA stepper layout ──────────────────────────────────────────────────
  if (displayStage === 2) {
    const mfaGradient = (className) => (
      <div className={className}>
        <button
          onClick={() => setDisplayStage(1)}
          className="inline-flex items-center gap-1.5 text-white/90 text-sm font-medium hover:text-white transition-colors mb-5"
        >
          <X size={16} strokeWidth={2.5} />
          Back
        </button>

        <div className="flex gap-1.5 mb-8">
          <div className="flex-1 h-1 rounded-full bg-white" />
          <div className="flex-1 h-1 rounded-full bg-white" />
          <div className="flex-1 h-1 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.25)' }} />
        </div>

        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
            <ShieldCheck size={22} className="text-white" strokeWidth={1.8} />
          </div>
          <h1 className="text-2xl font-bold text-white">Verify your identity</h1>
        </div>
      </div>
    );

    const mfaForm = (
      <>
        <h2 className="text-xl font-bold text-gray-900 mb-1.5">Enter your verification code</h2>
        <p className="text-sm text-gray-500 mb-6">We've emailed you a multi-factor authentication code. Enter it below to continue.</p>
        <MFACode setParentCode={setParentCode} loadingSpinner={loadingspinner} setLoadingSpinner={setLoadingSpinner} />
      </>
    );

    return (
      <>
        {/* Mobile */}
        <div className="lg:hidden min-h-screen flex flex-col" style={{ backgroundColor: '#F2F2F7' }}>
          <div className="w-full flex-shrink-0" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryDark})` }}>
            {mfaGradient("px-5 pt-5 pb-12")}
          </div>
          <div className="flex-1 -mt-6 relative z-10">
            <div className="bg-[#F2F2F7] rounded-t-[1.5rem] min-h-full">
              <div className="px-5 pt-7 pb-12">{mfaForm}</div>
            </div>
          </div>
        </div>

        {/* Desktop */}
        <div className="hidden lg:flex min-h-screen" style={{ backgroundColor: '#F2F2F7' }}>
          <div className="w-1/2 flex items-center justify-center p-12" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryDark})` }}>
            {mfaGradient("w-full max-w-md")}
          </div>
          <div className="w-1/2 flex items-center justify-center p-12">
            <div className="w-full max-w-md">{mfaForm}</div>
          </div>
        </div>
      </>
    );
  }

  // ── Normal login layout (stages 0 & 1) ───────────────────────────────────
  return (
    <div className="min-h-screen flex flex-col lg:flex-row lg:items-center" style={{ backgroundColor: '#F2F2F7' }}>

      {/* Left side (mobile: top, desktop: left half) — orbital animation */}
      <div className="lg:w-1/2 flex items-center justify-center pt-4 lg:pt-0">
        <div className="lg:scale-125">
          <OrbitalAnimation primaryColor={primaryColor} />
        </div>
      </div>

      {/* Right side (mobile: bottom, desktop: right half) — content */}
      <div className="lg:w-1/2 flex flex-col items-center justify-center px-6 pb-8 lg:pb-0">
        <div className="w-full max-w-sm mx-auto">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            {settings.logo_url ? (
              <img
                src={theme === 'dark' && settings.dark_mode_logo_url ? settings.dark_mode_logo_url : settings.logo_url}
                alt="logo"
                className="h-14 object-contain"
              />
            ) : (
              <h1 className="text-2xl font-bold text-gray-900">{settings.company_name}</h1>
            )}
            {!!settings.test_system && (
              <div className="mt-2 inline-flex rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-700">
                Test System
              </div>
            )}
          </div>

          {/* Stage 0: Welcome — Sign In + Register buttons */}
          {displayStage === 0 && (
            <>
              <button
                onClick={() => setDisplayStage(1)}
                className="w-full flex items-center justify-center rounded-2xl py-4 text-base font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
              >
                Sign In
              </button>

              {!!settings.registration_enabled && (
                <a
                  href="/register"
                  className="mt-3 w-full flex items-center justify-center rounded-2xl py-4 text-base font-semibold border-2 transition-opacity hover:opacity-80"
                  style={{ borderColor: primaryColor, color: primaryColor }}
                >
                  Register
                </a>
              )}

              {settings.user_help_link && (
                <p className="mt-6 text-center">
                  <a href={settings.user_help_link} target="_blank" rel="noreferrer" className="text-sm text-gray-500 hover:text-gray-700">
                    Need help?
                  </a>
                </p>
              )}

              <p className="mt-4 text-center text-xs text-gray-400 leading-relaxed">
                By using this service you agree to the<br />
                <a href="https://tenantwise.co.uk/eula" target="_blank" rel="noreferrer" className="font-semibold text-gray-500 hover:text-gray-700">
                  End User License Agreement
                </a>
              </p>
            </>
          )}

          {/* Stage 1: Login form */}
          {displayStage === 1 && (
            <>
              <form className="space-y-4" autoComplete="off" onSubmit={(e) => { e.preventDefault(); if (ssoEnabled && emailChecked) initiateSSO(); else initialLogin(); }}>
                <div>
                  <input
                    type="email"
                    id="login-email"
                    name="login-email"
                    autoComplete="off"
                    required
                    placeholder="Email address"
                    onChange={emailChange}
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
                  />
                </div>

                {!(ssoEnabled && emailChecked) && (
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="login-password"
                      name="login-password"
                      autoComplete="off"
                      required
                      placeholder="Password"
                      onChange={passwordChange}
                      onKeyPress={handleEnter}
                      className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3.5 pr-12 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                )}

                {ssoEnabled && emailChecked && (
                  <div className="rounded-xl bg-blue-50 p-3">
                    <p className="text-sm text-blue-700">
                      This account uses Entra ID Single Sign-On
                    </p>
                  </div>
                )}

                {!(ssoEnabled && emailChecked) && (
                  <div className="text-right">
                    <a href="/request-password-reset" className="text-sm font-medium" style={{ color: primaryColor }}>
                      Forgot password?
                    </a>
                  </div>
                )}

                <div>
                  {ssoEnabled && emailChecked ? (
                    <button
                      type="submit"
                      disabled={ssoLoading}
                      className="w-full flex items-center justify-center gap-2 rounded-2xl py-4 text-base font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {ssoLoading ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none"><path d="M10 0H0V10H10V0Z" fill="#F25022"/><path d="M21 0H11V10H21V0Z" fill="#7FBA00"/><path d="M10 11H0V21H10V11Z" fill="#00A4EF"/><path d="M21 11H11V21H21V11Z" fill="#FFB900"/></svg>
                          Sign in with Entra ID
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={loadingspinner}
                      className="w-full flex items-center justify-center rounded-2xl py-4 text-base font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {loadingspinner ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        'Sign In'
                      )}
                    </button>
                  )}
                </div>
              </form>

              <button
                onClick={() => setDisplayStage(0)}
                className="mt-4 w-full text-center text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                Back
              </button>
            </>
          )}

        </div>
      </div>

    </div>
  )
}
